import { useEffect, useState } from 'react';
import { User } from '../useAuthOrAnon';

type CheckAuthConfig = {
  apiBaseUrl?: string;
  checkInterval?: number;
};

type UseCheckAuthResponse = {
  user: User | null;
  anonymousId: string | null;
  loading: boolean;
  error: string | null;
};

/**
 * Custom hook that handles authentication checks via the /check-auth endpoint.
 * It will redirect to the auth server if needed and handle the response tokens.
 *
 * @param config Optional configuration object
 * @returns Object containing user data, loading state, and any errors
 */
export const useCheckAuth = (config?: CheckAuthConfig): UseCheckAuthResponse => {
  const AUTH_CHECK_INTERVAL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds
  const AUTH_CHECK_STORAGE_KEY = 'heygg_last_auth_check';

  // Check URL parameters before initializing state
  const searchParams = new URLSearchParams(window.location.search);
  const initialToken = searchParams.get('token');
  const initialAnonymousId = searchParams.get('anonymous_id');

  // Initialize state based on URL parameters first, then fallback to localStorage
  const [user, setUser] = useState<User | null>(() => {
    if (initialToken) {
      const storedUser = localStorage.getItem('heygg_user');
      return storedUser ? JSON.parse(storedUser) : null;
    }
    return null;
  });

  const [anonymousId, setAnonymousId] = useState<string | null>(() => {
    if (initialAnonymousId) {
      return initialAnonymousId;
    }
    if (initialToken) {
      return null;
    }
    return localStorage.getItem('heygg_anonymous_id');
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const apiBaseUrl = config?.apiBaseUrl || 'https://play.hey.gg';
  const checkInterval = config?.checkInterval || AUTH_CHECK_INTERVAL;

  useEffect(() => {
    const checkAuth = async () => {
      // Clean up URL if we have auth params
      if (initialToken || initialAnonymousId) {
        const cleanSearchParams = new URLSearchParams(window.location.search);
        cleanSearchParams.delete('token');
        cleanSearchParams.delete('anonymous_id');

        // Construct new URL without these params
        const newUrl = `${window.location.pathname}${
          cleanSearchParams.toString() ? `?${cleanSearchParams.toString()}` : ''
        }${window.location.hash}`;

        // Replace current URL without adding to browser history
        window.history.replaceState({}, '', newUrl);
      }

      const now = Date.now();

      // Handle incoming token
      if (initialToken) {
        localStorage.setItem('heygg_jwt', initialToken);
        try {
          const response = await fetch(`${apiBaseUrl}/api/user`, {
            headers: {
              Authorization: `Bearer ${initialToken}`,
              'Content-Type': 'application/json',
            },
          });

          if (!response.ok) {
            throw new Error('Failed to fetch user details');
          }

          const userData = await response.json();

          // Set global window properties for authenticated user
          window.eh2 = userData.email_eh2;
          window.ehttd = userData.email_ehttd;

          localStorage.setItem('heygg_user', JSON.stringify(userData));
          localStorage.removeItem('heygg_anonymous_id');
          setAnonymousId(null);
          setUser(userData);
        } catch (err) {
          setError(err instanceof Error ? err.message : 'Failed to fetch user data');
        }
        setLoading(false);
        localStorage.setItem(AUTH_CHECK_STORAGE_KEY, now.toString());
        return;
      }

      // Handle incoming anonymous ID
      if (initialAnonymousId) {
        localStorage.setItem('heygg_anonymous_id', initialAnonymousId);
        localStorage.removeItem('heygg_jwt');
        localStorage.removeItem('heygg_user');
        window.eh2 = '';
        window.ehttd = '';
        setUser(null);
        setAnonymousId(initialAnonymousId);
        setLoading(false);
        localStorage.setItem(AUTH_CHECK_STORAGE_KEY, now.toString());
        return;
      }

      // No incoming parameters - check existing state
      const existingJwt = localStorage.getItem('heygg_jwt');
      const existingAnonymousId = localStorage.getItem('heygg_anonymous_id');

      // If we have neither a JWT nor an anonymous ID, redirect to check-auth
      if (!existingJwt && !existingAnonymousId) {
        const currentUrl = window.location.href;
        const redirectParams = new URLSearchParams();
        redirectParams.set('redirect_uri', currentUrl);
        window.location.href = `${apiBaseUrl}/check-auth?${redirectParams.toString()}`;
        return;
      }

      // Validate existing JWT if present
      if (existingJwt) {
        try {
          const response = await fetch(`${apiBaseUrl}/api/user`, {
            headers: {
              Authorization: `Bearer ${existingJwt}`,
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            const userData = await response.json();
            window.eh2 = userData.email_eh2;
            window.ehttd = userData.email_ehttd;
            localStorage.setItem('heygg_user', JSON.stringify(userData));
            setUser(userData);
            setAnonymousId(null);
            setLoading(false);
            localStorage.setItem(AUTH_CHECK_STORAGE_KEY, now.toString());
            return;
          }

          // If JWT validation fails, clear it
          localStorage.removeItem('heygg_jwt');
          localStorage.removeItem('heygg_user');
          window.eh2 = '';
          window.ehttd = '';
          setUser(null);
        } catch (err) {
          console.warn('Failed to validate existing token:', err);
        }
      }

      const lastAuthCheck = parseInt(
        localStorage.getItem(AUTH_CHECK_STORAGE_KEY) || '0',
        10,
      );
      const shouldForceCheck = now - lastAuthCheck > checkInterval;

      // If we get here with an existing anonymous ID, use it
      if (existingAnonymousId && !shouldForceCheck) {
        setAnonymousId(existingAnonymousId);
        setLoading(false);
        return;
      }

      // If we get here, we have no valid auth - redirect to check-auth
      const currentUrl = window.location.href;
      const redirectParams = new URLSearchParams();
      redirectParams.set('redirect_uri', currentUrl);
      if (existingAnonymousId) {
        redirectParams.set('anonymous_id', existingAnonymousId);
      }
      window.location.href = `${apiBaseUrl}/check-auth?${redirectParams.toString()}`;
    };

    checkAuth();
  }, [apiBaseUrl, checkInterval, initialToken, initialAnonymousId]);

  return { user, anonymousId, loading, error };
};

export default useCheckAuth;
